import React, { useCallback, useEffect, useState } from 'react';
import { Card, Upload, message, Button, Image, Modal } from 'antd';
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { axiosBase } from 'utils';

const axios = axiosBase.create();
const S3_BASE_URL = 'https://msptrw.s3.us-east-2.amazonaws.com/rackphotos/';

const RackPhotoUpload = ({ title, moduleType, moduleId }) => {
  const [photos, setPhotos] = useState({ front: [], back: [] });
  const [loading, setLoading] = useState({ front: false, back: false });
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  useEffect(() => {
    fetchPhotos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const fetchPhotos = useCallback(() => {
    console.log('Fetching photos for moduleId:', moduleId);
    axios
      .get(`/rack-photos/module/${moduleId}`)
      .then((response) => {
        console.log('Photos response:', response.data);
        const photoMap = { front: [], back: [] };
        if (
          response.data &&
          response.data.items &&
          Array.isArray(response.data.items)
        ) {
          response.data.items.forEach((photo) => {
            if (photo.photo_type) {
              photoMap[photo.photo_type].push({
                ...photo,
                thumbnailUrl: `${S3_BASE_URL}${photo.thumbnail}`,
                fullUrl: `${S3_BASE_URL}${photo.photo}`,
              });
            }
          });
          // Sort by sequence
          photoMap.front.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
          photoMap.back.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        }
        console.log('Processed photo map:', photoMap);
        setPhotos(photoMap);
      })
      .catch((error) => {
        console.error('Error fetching photos:', error);
        message.error('Failed to fetch photos');
        setPhotos({ front: [], back: [] });
      });
  });

  const handleUpload = (file, photoType) => {
    if (photos[photoType].length >= 12) {
      message.error(`Maximum 12 ${photoType} photos allowed per rack`);
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('moduleType', moduleType);
    formData.append('moduleId', moduleId);
    formData.append('photoType', photoType);
    // Don't send sequence - let API auto-assign next available

    setLoading((prev) => ({ ...prev, [photoType]: true }));
    axios
      .post('/rack-photos/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(() => {
        message.success('Photo uploaded successfully');
        fetchPhotos();
      })
      .catch((error) => {
        console.error('Error uploading photo:', error);
        const errorMsg =
          error.response?.data?.message || 'Failed to upload photo';
        message.error(errorMsg);
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, [photoType]: false }));
      });
  };

  const handleDelete = (photoId) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this photo?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No, cancel',
      onOk() {
        return new Promise((resolve, reject) => {
          axios
            .delete(`/rack-photos/${photoId}`)
            .then(() => {
              message.success('Photo deleted successfully');
              fetchPhotos();
              resolve();
            })
            .catch((error) => {
              console.error('Error deleting photo:', error);
              message.error('Failed to delete photo');
              reject(error);
            });
        });
      },
    });
  };

  const handlePreview = (photo) => {
    window.open(photo.fullUrl, '_blank');
  };

  const renderPhotoCard = (photoType) => {
    const photoList = photos[photoType] || [];
    const canUploadMore = photoList.length < 12;
    console.log(`Rendering ${photoType} card with photos:`, photoList);

    const uploadProps = {
      name: 'photo',
      multiple: false,
      beforeUpload: (file) => {
        handleUpload(file, photoType);
        return false;
      },
      showUploadList: false,
      disabled: !canUploadMore,
    };

    return (
      <Card
        title={`${
          photoType.charAt(0).toUpperCase() + photoType.slice(1)
        } View (${photoList.length}/12)`}
        style={{ marginBottom: 16 }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 16,
          }}
        >
          {photoList.map((photo) => (
            <div
              key={photo._id}
              style={{
                position: 'relative',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  width: '100%',
                  height: 200,
                  overflow: 'hidden',
                }}
                onClick={() => handlePreview(photo)}
              >
                <Image
                  alt={`${photoType} ${photo.sequence || ''}`}
                  src={photo.thumbnailUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  preview={false}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
                >
                  <EyeOutlined style={{ fontSize: 24, color: 'white' }} />
                </div>
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  gap: 4,
                }}
              >
                <span
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  #{photo.sequence || ''}
                </span>
                <Button
                  type="primary"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(photo._id);
                  }}
                />
              </div>
            </div>
          ))}

          {canUploadMore && (
            <div
              style={{
                border: '2px dashed #d9d9d9',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                backgroundColor: '#fafafa',
              }}
            >
              <Upload {...uploadProps}>
                <Button
                  icon={<UploadOutlined />}
                  loading={loading[photoType]}
                  type="dashed"
                >
                  Add {photoType.charAt(0).toUpperCase() + photoType.slice(1)}{' '}
                  Photo
                </Button>
              </Upload>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="rack-photo-upload">
      <Card title={title}>
        {renderPhotoCard('front')}
        {renderPhotoCard('back')}
      </Card>
    </div>
  );
};

export default RackPhotoUpload;
